import { useNavigate, useLocation } from 'react-router-dom';

/** Resolve a public-folder asset URL respecting Vite's BASE_URL. */
const ICON_BASE = `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons/common-left-nav`;

/**
 * Main navigation registry.
 *
 * Top-level items render in the left rail. Items with a `children` array
 * show a secondary column (see `SubNav`) when they are the active area.
 * Keep the rail order in sync with the UX mock:
 *   Home → Devices → Checkout → Users → Comms → Licensing →
 *   Sites → System.
 *
 * Each item carries an `iconImg` pointing at a PNG in
 * `portal-ui/public/icons/common-left-nav/`. We keep the legacy `icon`
 * (Font Awesome class) as a fallback for items that don't yet have a
 * dedicated PNG (e.g. Network).
 */
export const navItems = [
  { id: 'home', path: '/home', iconImg: `${ICON_BASE}/home.svg`, icon: 'fa-home', label: 'Home' },
  {
    id: 'devices',
    path: '/devices',
    iconImg: `${ICON_BASE}/devices.svg`,
    icon: 'fa-mobile-alt',
    label: 'Devices',
    children: [
      { id: 'cameras',  path: '/devices/cameras',  label: 'Body cameras' },
      { id: 'docks',    path: '/devices/docks',    label: 'Docks' },
      { id: 'profiles', path: '/devices/profiles', label: 'Profiles' },
      { id: 'updates',  path: '/devices/updates',  label: 'Updates' },
      { id: 'networks', path: '/devices/networks', label: 'Network' },
    ],
  },
  // Checkout opens sub-navigation with:
  // - Checkouts list page inside portal chrome
  // - Checkout templates management page inside portal chrome
  // Launch actions still open /checkout/camera (full-bleed) in a new tab.
  {
    id: 'checkout',
    path: '/checkout',
    iconImg: `${ICON_BASE}/checkout.svg`,
    icon: 'fa-desktop',
    label: 'Checkout',
    children: [
      { id: 'checkout-checkouts', path: '/checkout/checkouts', label: 'Checkouts' },
      { id: 'checkout-templates', path: '/checkout/templates', label: 'Checkout templates' },
    ],
  },
  { id: 'users',       path: '/users',       iconImg: `${ICON_BASE}/user.svg`,     icon: 'fa-user',            label: 'Users',
    children: [
      { id: 'user-overview', path: '/users/overview', label: 'User overview' },
      { id: 'roles',         path: '/users/roles',    label: 'Roles' },
    ],
  },
  {
    id: 'comms',
    path: '/comms',
    iconImg: `${ICON_BASE}/mic.svg`,
    icon: 'fa-microphone',
    label: 'Comms',
    children: [
      { id: 'talk-groups', path: '/comms/talk-groups', label: 'Talk groups' },
      { id: 'teams',       path: '/comms/teams',       label: 'Teams' },
    ],
  },
  { id: 'licensing',   path: '/licensing',   iconImg: `${ICON_BASE}/license.svg`,  icon: 'fa-shield-alt',      label: 'Licensing' },
  { id: 'sites',       path: '/sites',       iconImg: `${ICON_BASE}/site.svg`,     icon: 'fa-map-marker-alt',  label: 'Sites' },
  { id: 'system',      path: '/system',      iconImg: `${ICON_BASE}/system.svg`,   icon: 'fa-sitemap',         label: 'System' },
  // DSM uses a text badge instead of a Font Awesome glyph (per UX mock).
  // Opens the Device State Monitor dashboard in a new tab.
  { id: 'dsm', path: '/dsm', iconText: 'DSM', label: 'DSM', newTab: true },
];

/** Returns the nav item whose path matches the current pathname. */
export function findActiveTopLevel(pathname) {
  return (
    navItems.find((item) => {
      if (item.newTab) return false;
      if (item.path === '/home') return pathname === '/home';
      return pathname === item.path || pathname.startsWith(`${item.path}/`);
    }) || null
  );
}

function Sidebar({ openedParentId, onOpenSub, onCloseSub }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (item) => {
    if (item.newTab) return false;
    // Show active while its sub-nav overlay is open, even before the user
    // picks a child route.
    if (openedParentId === item.id) return true;
    if (item.path === '/home') return location.pathname === '/home';
    return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
  };

  const handleBrandClick = () => {
    onCloseSub?.();
    navigate('/home');
  };

  const handleNavClick = (item) => {
    if (item.newTab) {
      onCloseSub?.();
      const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
      window.open(`${base}${item.path}`, '_blank', 'noopener,noreferrer');
      return;
    }
    // Items with children open the secondary overlay instead of navigating.
    // Clicking the same parent again toggles it closed.
    if (item.children && item.children.length > 0) {
      if (openedParentId === item.id) {
        onCloseSub?.();
      } else {
        onOpenSub?.(item.id);
      }
      return;
    }
    // Plain item — close any open sub-nav and navigate.
    onCloseSub?.();
    navigate(item.path);
  };

  return (
    <nav className="nav-sidebar">
      <div className="nav-brand" onClick={handleBrandClick} title="DaVinci">
        <img
          src={`${ICON_BASE}/logo.svg`}
          alt="DaVinci"
          className="nav-brand-img"
        />
      </div>

      <div className="nav-menu">
        {navItems.map((item) => (
          <div
            key={item.id}
            className={`nav-item ${isActive(item) ? 'active' : ''}`}
            onClick={() => handleNavClick(item)}
            title={item.label}
          >
            {item.iconText ? (
              <span className="nav-item-text-icon">{item.iconText}</span>
            ) : item.iconImg ? (
              <img src={item.iconImg} alt="" className="nav-item-img" />
            ) : (
              <i className={`fas ${item.icon}`}></i>
            )}
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      <div className="portal-footer">© {new Date().getFullYear()}</div>
    </nav>
  );
}

export default Sidebar;
